import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ILocationResponse } from '@kcftech/prometheus-client';
import { DocumentTitle } from 'components/DocumentTitle';
import { DetailsPage } from 'components/page/DetailsPage';
import { useFeatureFlag } from 'configcat-react';
import { AdvancedSettingsSection } from 'pages/Settings/V4SensorSettings/AdvancedSettingsSection';
import { CollectionSettingsSection } from 'pages/Settings/V4SensorSettings/CollectionSettingsSection';
import { useV4SensorSettings } from 'custom_hooks/data_hooks/v4_sensor_settings/useV4SensorSettings';
import { ExternalLink } from 'routing/ExternalLink';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const COLUMN_MAX_WIDTH = '82.6rem';

const PageContent = styled.div`
    padding: 4rem;
    overflow-y: auto;
`;

const PageHeader = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: ${COLUMN_MAX_WIDTH};
`;

const PageTitle = styled.h1`
    font-size: 2.4rem;
    font-weight: 500;
    line-height: 3rem;
    margin: 0;
    color: ${tokens.text.primary};
`;

const Callout = styled.div`
    background-color: ${tokens.surface.subtle};
    border-radius: 8px;
    padding: 0.8rem 1.2rem;
    color: ${tokens.text.primary};
    font-size: 1.4rem;
    line-height: 1.8rem;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
`;

const SettingsCard = styled.div`
    box-sizing: border-box;
    margin-top: 1.6rem;
    width: 100%;
    max-width: ${COLUMN_MAX_WIDTH};
    border: 1px solid ${tokens.border.default};
    border-radius: 8px;
    padding: 2rem;
    display: flex;
    flex-direction: column;

    > * + * {
        margin-top: 3.2rem;
    }
`;

type Props = Readonly<{
    location: ILocationResponse;
}>;

export function V4SensorSettingsPage({ location }: Props) {
    const { value: v4SensorsEnabled } = useFeatureFlag('v4Sensors', false);
    const {
        data: settings,
        isLoading,
        error,
    } = useV4SensorSettings({
        locationId: location.Id,
    });

    if (!v4SensorsEnabled) {
        return null;
    }

    return (
        <DetailsPage
            isLoading={isLoading}
            error={error ? 'Unable to load V4 sensor settings.' : undefined}
            fullHeight
            fullWidth
            noPageScroll
        >
            {settings && (
                <>
                    <DocumentTitle title={`${location.Name} V4 Sensor Settings`} />
                    <PageContent>
                        <PageHeader>
                            <PageTitle>[{location.Name}] V4 Sensor Settings</PageTitle>
                            <Callout>
                                <span>
                                    Changes apply to V4 Sensors at [{location.Name}] but
                                    will not override individually configured V4 sensors
                                </span>
                                {/* TODO: replace mock URL with the real V4 sensor user guide link once Design provides it. */}
                                <ExternalLink url="https://example.com/v4-sensor-user-guide">
                                    <span>
                                        See the V4 sensor user guide{' '}
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                        />
                                    </span>
                                </ExternalLink>
                            </Callout>
                        </PageHeader>
                        <SettingsCard>
                            <CollectionSettingsSection settings={settings} />
                            <AdvancedSettingsSection settings={settings} />
                        </SettingsCard>
                    </PageContent>
                </>
            )}
        </DetailsPage>
    );
}
